/**
 * React Query mutation hook for transitioning a component rule's status.
 *
 * Used by the activate / archive / (potentially) re-draft row actions on
 * the rules table. The server enforces the valid transition graph, so
 * the hook stays ignorant of the state machine — callers pass the target
 * status and handle server errors via the standard mutation error path.
 */

import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';

import type { RuleStatus } from '@components/shared/configurable';

import {
  componentRulesService,
  type ComponentRuleDTO,
} from '../../api/componentRulesService';
import { componentRulesKeys } from './queryKeys';

/** Variables accepted by `useUpdateComponentRuleStatus().mutate(...)`. */
export interface UpdateComponentRuleStatusVariables {
  id: string;
  status: RuleStatus;
  componentId?: string;
}

/** Mutation hook for `PATCH /claimflow/component-rules/{id}/status`. */
export function useUpdateComponentRuleStatus(): UseMutationResult<
  ComponentRuleDTO,
  Error,
  UpdateComponentRuleStatusVariables
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, status }: UpdateComponentRuleStatusVariables) =>
      componentRulesService.updateRuleStatus(id, status),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: componentRulesKeys.detail(variables.id),
      });
      void queryClient.invalidateQueries({
        queryKey: componentRulesKeys.lists(),
      });
      if (variables.componentId !== undefined) {
        void queryClient.invalidateQueries({
          queryKey: componentRulesKeys.list(variables.componentId),
        });
      }
    },
  });
}
